import { differenceInSeconds, format, isToday, isTomorrow } from 'date-fns';
import { router } from 'expo-router';
import { useCallback, useEffect, useMemo } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { IconChevronRight, IconTime, InlineAlert, Text, tokens } from '@fhs/ui';
import { useOrderContext } from '@fhs-legacy/frontend/src/state/order';
import { OPEN_PICKUP_TIME_MODAL } from '@fhs-legacy/frontend/src/utils/test-ids';

import { useDatePickerOptions } from './action-pickup-time-selector/use-date-time-picker-options';

export default function PickupTimeSection() {
  const { fireOrderIn, setFireOrderIn } = useOrderContext();
  const { datePickerData, isRestaurantOpen, firstAvailableDate } = useDatePickerOptions();

  // Always default fireOrderIn to the actual seconds until the first available time slot.
  // This matches the legacy behavior where calculateFireOrderInSeconds is always used —
  // fireOrderIn is never 0. The "ASAP" label is purely a display concern.
  useEffect(() => {
    if (!firstAvailableDate || fireOrderIn > 0) {
      return;
    }

    const secondsUntilFirstAvailable = differenceInSeconds(firstAvailableDate, new Date());
    if (secondsUntilFirstAvailable > 0) {
      setFireOrderIn(secondsUntilFirstAvailable);
    }
  }, [firstAvailableDate, fireOrderIn, setFireOrderIn]);

  // Calculate pickup time from fireOrderIn
  const pickupDate = useMemo(() => {
    if (!fireOrderIn) {
      return new Date();
    }
    // fireOrderIn is in seconds from now
    const fireOrderInMs = fireOrderIn * 1000;
    return getDateRoundUpToNearestFiveMinutes(new Date(Date.now() + fireOrderInMs));
  }, [fireOrderIn]);

  // Calculate first available time slot directly from datePickerData (same logic as TimePicker)
  const firstAvailableTimeSlot = useMemo(() => {
    if (!datePickerData || datePickerData.length === 0) {
      return null;
    }

    // Find the first available day
    const firstAvailableDay = datePickerData.find(
      day => !day.isUnavailable && day.options.length > 0
    );

    if (!firstAvailableDay?.options?.[0]) {
      return null;
    }

    return firstAvailableDay.options[0].time;
  }, [datePickerData]);

  const formatOrderTime = useCallback(
    (orderTime: Date) => {
      const time = format(orderTime, 'h:mm a');

      // If we don't have time slot data yet, default to ASAP
      if (!firstAvailableTimeSlot || !datePickerData) {
        return 'ASAP';
      }

      // Check if this is the ASAP time (first available timeslot only for today)
      // If fireOrderIn is 0 or not set it's an ASAP timeslot
      // Otherwise, check if orderTime matches firstAvailableTimeSlot when both are today
      const isAsapTime = (() => {
        // If fireOrderIn any sorts of falsy, it's ASAP
        if (!fireOrderIn || fireOrderIn === 0) {
          // Only show ASAP if the store is open and the first available time slot is today
          return isRestaurantOpen && isToday(firstAvailableTimeSlot);
        }

        // Otherwise, check if orderTime matches firstAvailableTimeSlot when both are today
        if (!firstAvailableTimeSlot || !isToday(firstAvailableTimeSlot) || !isToday(orderTime)) {
          return false;
        }

        // Round both times to 5 minutes using the same rounding method (ceil) as datePickerData
        // to ensure they're on the same boundary
        const roundedOrderTime = getDateRoundUpToNearestFiveMinutes(orderTime);
        const roundedFirstAvailable = getDateRoundUpToNearestFiveMinutes(firstAvailableTimeSlot);

        // Compare with 1-second tolerance (times should match exactly after rounding)
        const timesMatch =
          Math.abs(roundedOrderTime.getTime() - roundedFirstAvailable.getTime()) <= 1000;

        // Only show ASAP if times match AND store is open
        return timesMatch && isRestaurantOpen;
      })();

      if (isAsapTime) {
        return 'ASAP';
      } else if (isToday(orderTime)) {
        return `Today - ${time}`;
      } else if (isTomorrow(orderTime)) {
        return `Tomorrow - ${time}`;
      } else {
        return `${format(orderTime, 'eee')} - ${time}`;
      }
    },
    [firstAvailableTimeSlot, datePickerData, fireOrderIn, isRestaurantOpen]
  );

  return (
    <>
      <Pressable
        style={[styles.linkSection, styles.sectionBorder]}
        onPress={() => router.setParams({ action: 'time' })}
        testID={OPEN_PICKUP_TIME_MODAL}
      >
        {({ pressed }) => (
          <View style={styles.section}>
            <View style={[styles.iconCircle, pressed && styles.iconCirclePressed]}>
              <IconTime size={24} />
            </View>

            <View style={styles.flex1}>
              <Text.Ui size="sm">Pick up time:</Text.Ui>
              <Text.Ui
                style={{
                  marginBottom: 4,
                }}
                size="md"
                weight="bold"
              >
                {formatOrderTime(pickupDate)}
              </Text.Ui>
            </View>

            <View style={styles.timeAndArrow}>
              <IconChevronRight size={24} />
            </View>
          </View>
        )}
      </Pressable>
      {!isRestaurantOpen && (
        <View style={styles.alertContainer}>
          <InlineAlert
            type="warning"
            message="Planning to place an order for later? Make sure this date and time works for you!"
          />
        </View>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  linkSection: {
    width: '100%',
  },
  flex1: { flex: 1 },
  timeAndArrow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },

  section: {
    width: '100%',
    gap: 8,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },
  sectionBorder: {
    width: '100%',
    borderBottomColor: tokens.colors.$black10,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },

  iconCirclePressed: {
    backgroundColor: tokens.colors.$black10,
  },
  iconCircle: {
    height: 40,
    width: 40,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: tokens.colors.$houseLight,
  },
  alertContainer: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 16,
    borderBottomColor: tokens.colors.$black10,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
});

function getDateRoundUpToNearestFiveMinutes(date = new Date()): Date {
  const ms = 1000 * 60 * 5; // convert 5 minutes to ms
  // Use Math.ceil to match the rounding used in use-date-time-picker-options.ts
  // This ensures consistent rounding behavior when comparing times
  const roundedDate = new Date(Math.ceil(date.getTime() / ms) * ms);
  return roundedDate;
}
